import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Filter, X, CheckCircle, Recycle, Award, SlidersHorizontal } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { productAPI, categoryAPI } from '../api';

const conditionOptions = [
  { id: 'new_aftermarket', name: 'New Aftermarket', icon: CheckCircle, color: 'text-emerald-600' },
  { id: 'used_oem', name: 'Used OEM', icon: Recycle, color: 'text-amber-600' },
  { id: 'refurbished', name: 'Refurbished', icon: Award, color: 'text-blue-600' },
];

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditionCounts, setConditionCounts] = useState({});

  const category = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const year = searchParams.get('year') || '';
  const make = searchParams.get('make') || '';
  const model = searchParams.get('model') || '';
  const brand = searchParams.get('brand') || '';
  const condition = searchParams.get('condition') || '';
  const capaOnly = searchParams.get('capa') === 'true';
  const sort = searchParams.get('sort') || 'name';
  const page = parseInt(searchParams.get('page') || '1');

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 16, sort };
      if (category) params.category = category;
      if (search) params.search = search;
      if (year) params.year = year;
      if (make) params.make = make;
      if (model) params.model = model;
      if (brand) params.brand = brand;
      if (condition) params.condition = condition;
      if (capaOnly) params.capa_certified = true;
      const { data } = await productAPI.getProducts(params);
      setProducts(data.products);
      setTotal(data.total);
      setPages(data.pages);
      setConditionCounts(data.condition_counts || {});
    } catch {} finally { setLoading(false); }
  }, [category, search, year, make, model, brand, condition, capaOnly, sort, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => {
    categoryAPI.getCategories().then(r => setCategories(r.data)).catch(() => {});
    productAPI.getBrands().then(r => setBrands(r.data)).catch(() => {});
  }, []);

  const updateParam = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) { newParams.set(key, value); } else { newParams.delete(key); }
    if (key !== 'page') newParams.delete('page');
    setSearchParams(newParams);
  };

  const clearFilters = () => setSearchParams({});
  const activeFilters = [category, search, year, make, model, brand, condition, capaOnly ? 'capa' : ''].filter(Boolean).length;
  const currentCategory = categories.find(c => c.id === category);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="products-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-[#0F766E] transition-colors">Home</Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">{currentCategory?.name || 'All Products'}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
            {search ? `Results for "${search}"` : currentCategory?.name || 'All Products'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{total} product{total !== 1 ? 's' : ''} found</p>
          {(year || make || model) && (
            <p className="text-sm text-[#0F766E] font-medium mt-1">Vehicle: {[year, make, model].filter(Boolean).join(' ')}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setFilterOpen(!filterOpen)} className="lg:hidden flex items-center gap-1.5 text-sm font-medium text-gray-700 border rounded px-3 py-2 hover:bg-gray-50" data-testid="filter-toggle-btn">
            <SlidersHorizontal size={16} /> Filters {activeFilters > 0 && `(${activeFilters})`}
          </button>
          <select value={sort} onChange={e => updateParam('sort', e.target.value)} className="border rounded px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#0F766E]/20 focus:border-[#0F766E]" data-testid="sort-select">
            <option value="name">Name A-Z</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="newest">Newest First</option>
          </select>
        </div>
      </div>

      {/* Active filter chips */}
      {activeFilters > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="active-filters">
          {category && <span className="bg-[#0F766E]/10 text-[#0F766E] text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1">{currentCategory?.name} <X size={14} className="cursor-pointer hover:text-red-500" onClick={() => updateParam('category', '')} /></span>}
          {search && <span className="bg-[#0F766E]/10 text-[#0F766E] text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1">"{search}" <X size={14} className="cursor-pointer hover:text-red-500" onClick={() => updateParam('search', '')} /></span>}
          {condition && <span className="bg-[#0F766E]/10 text-[#0F766E] text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1">{conditionOptions.find(c => c.id === condition)?.name} <X size={14} className="cursor-pointer hover:text-red-500" onClick={() => updateParam('condition', '')} /></span>}
          {capaOnly && <span className="bg-[#0F766E]/10 text-[#0F766E] text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1">CAPA <X size={14} className="cursor-pointer hover:text-red-500" onClick={() => updateParam('capa', '')} /></span>}
          {brand && <span className="bg-[#0F766E]/10 text-[#0F766E] text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1">{brand} <X size={14} className="cursor-pointer hover:text-red-500" onClick={() => updateParam('brand', '')} /></span>}
          {year && <span className="bg-[#0F766E]/10 text-[#0F766E] text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1">{year} {make} {model} <X size={14} className="cursor-pointer hover:text-red-500" onClick={clearFilters} /></span>}
          <button onClick={clearFilters} className="text-xs text-red-600 font-medium hover:underline" data-testid="clear-filters-btn">Clear All</button>
        </div>
      )}

      <div className="flex gap-6">
        {/* Sidebar */}
        <aside className={`${filterOpen ? 'fixed inset-0 z-50 bg-white p-6 overflow-y-auto' : 'hidden'} lg:block lg:static lg:w-60 shrink-0`} data-testid="filter-sidebar">
          <div className="flex items-center justify-between mb-4 lg:hidden">
            <h3 className="font-bold text-lg">Filters</h3>
            <button onClick={() => setFilterOpen(false)}><X size={24} /></button>
          </div>

          {/* Condition Filter */}
          <div className="mb-6">
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Condition</h4>
            <div className="space-y-1">
              <button onClick={() => updateParam('condition', '')}
                className={`block w-full text-left text-sm py-2 px-2 rounded transition-colors ${!condition ? 'bg-[#0F766E] text-white font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}
                data-testid="filter-condition-all">
                All ({Object.values(conditionCounts).reduce((a, b) => a + b, 0) || total})
              </button>
              {conditionOptions.map(opt => {
                const Icon = opt.icon;
                const count = conditionCounts[opt.id] || 0;
                return (
                  <button key={opt.id} onClick={() => updateParam('condition', opt.id)}
                    className={`flex items-center gap-2 w-full text-left text-sm py-2 px-2 rounded transition-colors ${condition === opt.id ? 'bg-[#0F766E] text-white font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}
                    data-testid={`filter-condition-${opt.id}`}>
                    <Icon size={14} className={condition === opt.id ? 'text-white' : opt.color} />
                    {opt.name} <span className="text-xs opacity-60">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* CAPA Filter */}
          <div className="mb-6">
            <button onClick={() => updateParam('capa', capaOnly ? '' : 'true')}
              className={`flex items-center gap-2 w-full text-left text-sm py-2 px-2 rounded border transition-colors ${capaOnly ? 'bg-[#0F766E] text-white border-[#0F766E]' : 'text-gray-700 border-gray-200 hover:bg-gray-50'}`}
              data-testid="filter-capa">
              <Award size={14} /> CAPA Certified Only
            </button>
          </div>

          {/* Categories */}
          <div className="mb-6">
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Category</h4>
            <div className="space-y-1">
              <button onClick={() => updateParam('category', '')}
                className={`block w-full text-left text-sm py-1.5 px-2 rounded ${!category ? 'bg-[#0F766E] text-white font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}
                data-testid="filter-category-all">All</button>
              {categories.map(cat => (
                <button key={cat.id} onClick={() => updateParam('category', cat.id)}
                  className={`block w-full text-left text-sm py-1.5 px-2 rounded ${category === cat.id ? 'bg-[#0F766E] text-white font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}
                  data-testid={`filter-category-${cat.id}`}>{cat.name}</button>
              ))}
            </div>
          </div>

          {/* Brands */}
          <div className="mb-6">
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Brand</h4>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              <button onClick={() => updateParam('brand', '')}
                className={`block w-full text-left text-sm py-1.5 px-2 rounded ${!brand ? 'font-semibold text-[#0F766E]' : 'text-gray-700 hover:bg-gray-100'}`}
                data-testid="filter-brand-all">All</button>
              {brands.map(b => (
                <button key={b} onClick={() => updateParam('brand', b)}
                  className={`block w-full text-left text-sm py-1.5 px-2 rounded ${brand === b ? 'font-semibold text-[#0F766E]' : 'text-gray-700 hover:bg-gray-100'}`}
                  data-testid={`filter-brand-${b}`}>{b}</button>
              ))}
            </div>
          </div>

          {filterOpen && (
            <button onClick={() => setFilterOpen(false)} className="w-full bg-[#0F766E] text-white font-bold py-3 rounded mt-4 lg:hidden" data-testid="apply-filters-btn">Apply Filters</button>
          )}
        </aside>

        {/* Products Grid */}
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white border rounded animate-pulse">
                  <div className="aspect-[4/3] bg-gray-200" />
                  <div className="p-4 space-y-3"><div className="h-3 bg-gray-200 rounded w-1/3" /><div className="h-4 bg-gray-200 rounded w-3/4" /><div className="h-6 bg-gray-200 rounded w-1/3" /><div className="h-10 bg-gray-200 rounded" /></div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20" data-testid="no-products">
              <Filter size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>No products found</h3>
              <p className="text-gray-500 text-sm mb-4">Try adjusting your filters or search terms</p>
              <button onClick={clearFilters} className="text-[#0F766E] font-semibold text-sm hover:underline">Clear filters</button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="products-grid">
                {products.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
              {pages > 1 && (
                <div className="flex justify-center gap-2 mt-8" data-testid="pagination">
                  {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => updateParam('page', p.toString())}
                      className={`w-10 h-10 rounded text-sm font-medium transition-colors ${p === page ? 'bg-[#0F766E] text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'}`}
                      data-testid={`page-${p}`}>{p}</button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
